import { memo, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Button, Popover, PopoverTrigger, PopoverSurface, Tooltip, mergeClasses, tokens } from '@coreai/fluentui-react';
import { DeleteRegular, ColorRegular } from '@fluentui/react-icons';
import { NodeResizer, type NodeProps } from '@xyflow/react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useAppStore } from '@/stores/appStore';
import { NoteMarkdownRenderer } from './NoteMarkdownRenderer';
import { useNoteStyles } from './NoteNode.styles';
import { NOTE_COLOR_KEYS, DEFAULT_NOTE_SIZE, getNoteColors } from '@/types/note';
import { ottoTokens } from '@/theme/ottoTokens';

interface NoteNodeProps extends NodeProps {
  id: string;
  dragging: boolean;
  selected: boolean;
}

export const NoteNode = memo(function NoteNode({ id: nodeId, dragging, selected }: NoteNodeProps) {
  const styles = useNoteStyles();
  const theme = useAppStore((s) => s.theme);
  const isDarkMode = theme === 'dark';
  // Strip 'note-' prefix — React Flow id is 'note-<uuid>', store key is '<uuid>'
  const noteId = nodeId.replace('note-', '');
  const note = useWorkflowStore((s) => s.workflow?.notes?.[noteId]);
  const updateNote = useWorkflowStore((s) => s.updateNote);
  const deleteNote = useWorkflowStore((s) => s.deleteNote);
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  // Local draft avoids store updates on every keystroke (which would re-render all nodes)
  const [draft, setDraft] = useState('');
  const draftRef = useRef(draft);
  const isEditingRef = useRef(isEditing);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    draftRef.current = draft;
    isEditingRef.current = isEditing;
  }, [draft, isEditing]);

  useEffect(
    () => () => {
      if (isEditingRef.current) {
        updateNote(noteId, { content: draftRef.current });
      }
    },
    [noteId, updateNote],
  );

  // Auto-focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // Select all text if it's the default placeholder
      if (textareaRef.current.value === '' || textareaRef.current.value === 'Type a note…') {
        textareaRef.current.select();
      }
    }
  }, [isEditing]); // Removed draft to prevent re-focusing on every keystroke

  // Handle resize end - persist dimensions and position to store
  // Position changes when resizing from top or left edges
  const handleResizeEnd = useCallback(
    (_event: unknown, params: { width: number; height: number; x: number; y: number }) => {
      updateNote(noteId, {
        metadata: {
          width: Math.round(params.width),
          height: Math.round(params.height),
          position: { x: Math.round(params.x), y: Math.round(params.y) },
        },
      } as Parameters<typeof updateNote>[1]);
    },
    [noteId, updateNote]
  );

  // Double-click to enter edit mode
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).tagName === 'A') return;
      e.preventDefault();
      e.stopPropagation();
      setDraft(note?.content ?? '');
      setIsEditing(true);
    },
    [note?.content]
  );

  const handleBlur = useCallback(() => {
    // Persist draft to store on blur
    updateNote(noteId, { content: draft });
    setIsEditing(false);
  }, [noteId, draft, updateNote]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDraft(e.target.value);
    },
    []
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Escape to exit edit mode
      if (e.key === 'Escape') {
        handleBlur();
      }
    },
    [handleBlur]
  );

  const handleDelete = useCallback(() => {
    deleteNote(noteId);
  }, [noteId, deleteNote]);

  const handleColorChange = useCallback(
    (colorKey: string) => {
      updateNote(noteId, { color: colorKey });
      setIsColorPickerOpen(false);
    },
    [noteId, updateNote]
  );

  // Get colors from palette based on theme (fallback to default yellow if invalid)
  const colorConfig = getNoteColors(note?.color || 'yellow', isDarkMode);
  const showToolbar = isHovered || selected || isColorPickerOpen;

  const cardStyle = useMemo(() => ({
    backgroundColor: colorConfig.background,
    border: `1px solid ${colorConfig.border}`,
    color: isDarkMode ? tokens.colorNeutralForeground1 : ottoTokens.noteForeground,
  }), [colorConfig, isDarkMode]);

  const textareaStyle = useMemo(() => ({ 
    color: isDarkMode ? tokens.colorNeutralForeground1 : ottoTokens.noteForeground 
  }), [isDarkMode]);

  if (!note) return null;

  return (
    <div
      className={mergeClasses(
        styles.noteWrapper,
        (isHovered || selected) && styles.resizerVisible
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <NodeResizer
        minWidth={DEFAULT_NOTE_SIZE.minWidth}
        minHeight={DEFAULT_NOTE_SIZE.minHeight}
        onResizeEnd={handleResizeEnd}
        isVisible={isHovered || selected}
      />
      <div
        className={mergeClasses(
          styles.noteCard,
          selected && styles.selected,
          dragging && styles.draggingNote
        )}
        style={cardStyle}
      >
        {isEditing ? (
          <textarea
            ref={textareaRef}
            className={mergeClasses(styles.noteTextarea, 'nodrag')}
            style={textareaStyle}
            value={draft}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder="Type a note…"
            aria-label="Note content"
          />
        ) : (
          <div
            className={mergeClasses(styles.markdownContainer, 'note-drag-handle')}
            onDoubleClick={handleDoubleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setDraft(note?.content ?? '');
                setIsEditing(true);
              }
            }}
            aria-label="Double-click to edit note"
          >
            {note.content ? (
              <NoteMarkdownRenderer content={note.content} isDarkMode={isDarkMode} />
            ) : (
              <span className={styles.placeholder}>Double-click to edit…</span>
            )}
          </div>
        )}
      </div>

      {/* Toolbar - below the note card */}
      <div className={mergeClasses(
        styles.noteToolbar,
        showToolbar && styles.noteToolbarVisible
      )}>
        <Popover
          open={isColorPickerOpen}
          onOpenChange={(_, data) => setIsColorPickerOpen(data.open)}
        >
          <PopoverTrigger>
            <Tooltip content="Change color" relationship="label">
              <Button
                className={styles.toolbarButton}
                appearance="subtle"
                size="small"
                icon={<ColorRegular />}
                aria-label="Change color"
              />
            </Tooltip>
          </PopoverTrigger>
          <PopoverSurface>
            <div className={styles.colorPickerContent}>
              {NOTE_COLOR_KEYS.map((colorKey) => {
                const swatchColors = getNoteColors(colorKey, isDarkMode);
                return (
                  <div
                    key={colorKey}
                    className={mergeClasses(
                      styles.colorSwatch,
                      note.color === colorKey && styles.colorSwatchSelected
                    )}
                    style={{
                      backgroundColor: swatchColors.background,
                      border: `1px solid ${swatchColors.border}`,
                    }}
                    onClick={() => handleColorChange(colorKey)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleColorChange(colorKey);
                      }
                    }}
                    aria-label={`Color ${colorKey}`}
                  />
                );
              })}
            </div>
          </PopoverSurface>
        </Popover>

        <Tooltip content="Delete note" relationship="label">
          <Button
            className={styles.toolbarButton}
            appearance="subtle"
            size="small"
            icon={<DeleteRegular />}
            onClick={handleDelete}
            aria-label="Delete note"
          />
        </Tooltip>
      </div>
    </div>
  );
});

export default NoteNode;
